// src/pages/AwakenPage.js — v4
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { doc, setDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../lib/firebase";
import { useAuth } from "../hooks/useAuth";
import { QUIZ_STEPS, PERKS_LIST, computeDemon } from "../data/demons";
import DemonCard from "../components/DemonCard";
import "./AwakenPage.css";

export default function AwakenPage() {
  const { user, profile, refreshProfile } = useAuth();
  const navigate = useNavigate();

  const [step, setStep]         = useState(0);
  const [answers, setAnswers]   = useState({});
  const [perks, setPerks]       = useState([]);
  const [demon, setDemon]       = useState(null);
  const [saving, setSaving]     = useState(false);
  const [perkError, setPerkError] = useState(false);
  const [confirmOverwrite, setConfirmOverwrite] = useState(false);
  const [revealPhase, setRevealPhase] = useState(0); // 0=rien, 1=kanji, 2=image, 3=texte, 4=fini

  const totalSteps  = QUIZ_STEPS.length + 1;
  const progress    = Math.round((step / totalSteps) * 100);
  const isPerksStep = step === QUIZ_STEPS.length;
  const isResult    = step === totalSteps;
  const currentQ    = (!isPerksStep && !isResult) ? QUIZ_STEPS[step] : null;

  const selectAnswer = (value) => setAnswers(prev => ({ ...prev, [currentQ.id]: value }));
  const togglePerk   = (id) => {
    setPerkError(false);
    setPerks(prev => prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id]);
  };
  const canNext = isPerksStep ? perks.length >= 3 : !!answers[currentQ?.id];

  const next = () => {
    if (isPerksStep) {
      if (perks.length < 3) { setPerkError(true); return; }
      setDemon(computeDemon(answers, perks));
      setStep(s => s + 1);
    } else {
      setStep(s => s + 1);
    }
  };
  const prev = () => setStep(s => Math.max(0, s - 1));

  const doSave = async () => {
    if (!user) { navigate("/auth?redirect=/awaken"); return; }
    setSaving(true);
    setConfirmOverwrite(false);
    try {
      const isFirst  = !profile?.demon;
      const newXp    = (profile?.xp || 0) + (isFirst ? 50 : 25);
      const newBadges = [...(profile?.badges || [])];
      if (isFirst && !newBadges.includes("first_demon")) newBadges.push("first_demon");

      await setDoc(doc(db, "users", user.uid), {
        demon: { ...demon, savedAt: serverTimestamp() },
        xp: newXp,
        badges: newBadges,
      }, { merge: true });
      await refreshProfile();
      navigate("/profile");
    } catch(e) { console.error(e); setSaving(false); }
  };

  const saveDemon = () => {
    if (!user) { navigate("/auth?redirect=/awaken"); return; }
    // Si démon existant → demander confirmation
    if (profile?.demon) { setConfirmOverwrite(true); return; }
    doSave();
  };

  const restart = () => { setStep(0); setAnswers({}); setPerks([]); setDemon(null); setConfirmOverwrite(false); setRevealPhase(0); };

  // Séquence de révélation théâtrale quand le démon apparaît
  useEffect(() => {
    if (!isResult || !demon) return;
    setRevealPhase(0);
    const t1 = setTimeout(() => setRevealPhase(1), 200);   // kanji apparaît
    const t2 = setTimeout(() => setRevealPhase(2), 1100);  // illustration se révèle
    const t3 = setTimeout(() => setRevealPhase(3), 2000);  // nom + tagline
    const t4 = setTimeout(() => setRevealPhase(4), 2700);  // carte + actions
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); clearTimeout(t4); };
  }, [isResult, demon]);

  return (
    <div className="awaken-page">
      <div className="awaken-bg" aria-hidden/>
      <div className="section" style={{paddingTop:"7rem"}}>

        <div className="awaken-header">
          <span className="section-eyebrow">覚醒 · Le Réveil</span>
          <h1 className="section-title">Trouve ton Démon</h1>
          <div className="divider divider-center"/>
          {!isResult && <p className="awaken-sub">5 questions. Pas de bonnes réponses. Ton démon sait déjà qui tu es.</p>}
        </div>

        {/* Progress */}
        {!isResult && (
          <div className="quiz-progress">
            <div className="progress-bar">
              <div className="progress-fill" style={{width: progress + "%"}}/>
            </div>
            <span className="progress-label">{step} / {totalSteps}</span>
          </div>
        )}

        {/* Quiz card */}
        {!isResult && (
          <div className="quiz-card">
            {currentQ && (
              <div className="quiz-step">
                <div className="step-badge">ÉTAPE {step + 1} / {totalSteps}</div>
                <h2 className="quiz-question">{currentQ.question}</h2>
                <p className="quiz-sub">{currentQ.subtitle}</p>
                <div className="quiz-options">
                  {currentQ.options.map(opt => (
                    <button
                      key={opt.value}
                      className={`quiz-option${answers[currentQ.id] === opt.value ? " selected" : ""}`}
                      onClick={() => selectAnswer(opt.value)}
                    >
                      <span className="opt-icon">{opt.icon}</span>
                      <div className="opt-text">
                        <strong>{opt.label}</strong>
                        <span>{opt.desc}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {isPerksStep && (
              <div className="quiz-step">
                <div className="step-badge">ÉTAPE {step + 1} / {totalSteps} · PERKS</div>
                <h2 className="quiz-question">Tes perks démoniaques.</h2>
                <p className="quiz-sub">Sélectionne tous ceux qui te définissent. Honnêteté totale requise.</p>
                <div className="perks-count-row">
                  <span className="perks-count">{perks.length} sélectionné{perks.length > 1 ? "s" : ""}</span>
                  {perkError && (
                    <span className="perks-error">⚠ Sélectionne au moins 3 perks</span>
                  )}
                </div>
                <div className="perks-select-grid" style={{marginTop:"1rem"}}>
                  {PERKS_LIST.map(p => (
                    <button
                      key={p.id}
                      className={`perk-select-btn${perks.includes(p.id) ? " active" : ""}`}
                      onClick={() => togglePerk(p.id)}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="quiz-nav">
              <div>
                {step > 0 && <button className="btn btn-ghost btn-sm" onClick={prev}>← RETOUR</button>}
              </div>
              <button className="btn btn-primary btn-sm" onClick={next} disabled={!canNext}>
                {isPerksStep ? "RÉVÉLER MON DÉMON 🔥" : "SUIVANT →"}
              </button>
            </div>
          </div>
        )}

        {/* Result */}
        {isResult && demon && (
          <div className="result-area">
            <div className="result-reveal-text">
              <span className={`section-eyebrow reveal-phase reveal-phase-1${revealPhase >= 1 ? " in" : ""}`}>
                覚醒 · Ton démon est apparu
              </span>
              <div className="result-oni-header">
                {demon.oni.image ? (
                  <div className={`result-oni-img-frame reveal-phase reveal-phase-2${revealPhase >= 2 ? " in" : ""}`}>
                    <img
                      src={`${demon.oni.image}?v=${Date.now()}`}
                      alt={demon.oni.name}
                      className="result-oni-img"
                      crossOrigin="anonymous"
                    />
                    <div className="result-oni-img-flash" aria-hidden/>
                  </div>
                ) : (
                  <span className={`result-oni-emoji-big reveal-phase reveal-phase-2${revealPhase >= 2 ? " in" : ""}`}>{demon.oni.emoji}</span>
                )}
                <div className={`reveal-phase reveal-phase-3${revealPhase >= 3 ? " in" : ""}`}>
                  <h2 className="result-oni-jp">{demon.oni.jp}</h2>
                  <p className="result-oni-name">{demon.oni.name.toUpperCase()}</p>
                  <p className="result-oni-tagline">"{demon.oni.tagline}"</p>
                </div>
              </div>
            </div>

            <div className={`reveal-phase reveal-phase-4${revealPhase >= 4 ? " in" : ""}`}>
              <DemonCard demon={demon} xp={0} />
            </div>

            {/* Confirmation d'écrasement */}
            {confirmOverwrite && (
              <div className="result-overwrite-confirm">
                <p>⚠️ Tu as déjà un démon — <strong>{profile.demon.oni?.name}</strong>. Le remplacer ?</p>
                <div style={{display:"flex", gap:"8px", marginTop:"10px"}}>
                  <button className="btn btn-primary btn-sm" onClick={doSave} disabled={saving}>
                    {saving ? <><span className="spinner"/> SAUVEGARDE...</> : "OUI, REMPLACER →"}
                  </button>
                  <button className="btn btn-ghost btn-sm" onClick={() => setConfirmOverwrite(false)}>ANNULER</button>
                </div>
              </div>
            )}

            {!confirmOverwrite && (
              <div className={`result-actions reveal-phase reveal-phase-4${revealPhase >= 4 ? " in" : ""}`}>
                {user
                  ? <button className="btn btn-primary" onClick={saveDemon} disabled={saving}>
                      {saving ? <><span className="spinner"/> SAUVEGARDE...</> : "SAUVEGARDER MON DÉMON →"}
                    </button>
                  : <button className="btn btn-primary" onClick={() => navigate("/auth?redirect=/awaken")}>
                      CRÉER UN COMPTE POUR SAUVEGARDER →
                    </button>
                }
                <button className="btn btn-ghost" onClick={restart}>RECOMMENCER ↺</button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
